import axios from 'axios';
import apiClient from '@/api/apiClient';
import type { Permission, PermissionInput, Role, RoleInput } from '../types';

interface ApiResponse<T> {
  code: number | string;
  message: string;
  result: T;
}

interface PaginatedResult<T> {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalElements: number;
  data: T[];
}

interface RoleDto {
  id: number;
  name: string;
  description: string;
  permissions?: Permission[];
}

interface ListQuery {
  page?: number;
  size?: number;
  sort?: string;
}

const PAGE_SIZE = 100;
const PERMISSIONS_PATH = '/api/permissions';
const ROLES_PATH = '/api/roles';

interface ApiErrorData {
  message?: string;
  error?: string;
}

const toAccessControlError = (error: unknown): Error => {
  if (axios.isAxiosError<ApiErrorData>(error)) {
    return new Error(
      error.response?.data?.message
        || error.response?.data?.error
        || (error.code === 'ECONNABORTED'
          ? 'Yêu cầu đã quá thời gian chờ. Vui lòng thử lại.'
          : error.response
            ? 'Máy chủ không thể thực hiện yêu cầu.'
            : 'Không thể kết nối đến máy chủ.'),
    );
  }

  return error instanceof Error ? error : new Error('Đã xảy ra lỗi không xác định.');
};

const getPermissionPage = async (query: ListQuery): Promise<PaginatedResult<Permission>> => {
  try {
    const response = await apiClient.get<ApiResponse<PaginatedResult<Permission>>>(PERMISSIONS_PATH, {
      params: query,
    });
    return response.data.result;
  } catch (error: unknown) {
    throw toAccessControlError(error);
  }
};

const getRolePage = async (query: ListQuery): Promise<PaginatedResult<Role>> => {
  try {
    const response = await apiClient.get<ApiResponse<PaginatedResult<RoleDto>>>(ROLES_PATH, {
      params: query,
    });
    return {
      ...response.data.result,
      data: response.data.result.data.map((role) => ({
        ...role,
        permissions: role.permissions ?? [],
      })),
    };
  } catch (error: unknown) {
    throw toAccessControlError(error);
  }
};

const getEveryPage = async <T>(
  getPage: (query: ListQuery) => Promise<PaginatedResult<T>>,
): Promise<T[]> => {
  const firstPage = await getPage({ page: 1, size: PAGE_SIZE, sort: 'id' });
  if (firstPage.totalPages <= 1) return firstPage.data;

  const remainingPages = await Promise.all(
    Array.from({ length: firstPage.totalPages - 1 }, (_, index) =>
      getPage({ page: index + 2, size: PAGE_SIZE, sort: 'id' })),
  );

  return [firstPage, ...remainingPages].flatMap((page) => page.data);
};

export const getAllPermissions = (): Promise<Permission[]> => getEveryPage(getPermissionPage);

export const getAllRoles = (): Promise<Role[]> => getEveryPage(getRolePage);

export const createPermission = async (input: PermissionInput): Promise<void> => {
  try {
    await apiClient.post<ApiResponse<Permission>>(PERMISSIONS_PATH, input);
  } catch (error: unknown) {
    throw toAccessControlError(error);
  }
};

export const updatePermission = async (
  permissionId: number,
  input: PermissionInput,
): Promise<void> => {
  try {
    await apiClient.put<ApiResponse<Permission>>(`${PERMISSIONS_PATH}/${permissionId}`, input);
  } catch (error: unknown) {
    throw toAccessControlError(error);
  }
};

export const deletePermission = async (permissionId: number): Promise<void> => {
  try {
    await apiClient.delete<ApiResponse<void>>(`${PERMISSIONS_PATH}/${permissionId}`);
  } catch (error: unknown) {
    throw toAccessControlError(error);
  }
};

export const createRole = async (input: RoleInput): Promise<void> => {
  try {
    await apiClient.post<ApiResponse<Role>>(ROLES_PATH, input);
  } catch (error: unknown) {
    throw toAccessControlError(error);
  }
};

export const updateRolePermissions = async (
  roleId: number,
  permissionIds: number[],
): Promise<void> => {
  try {
    await apiClient.put<ApiResponse<Role>>(`${ROLES_PATH}/${roleId}`, { permissionIds });
  } catch (error: unknown) {
    throw toAccessControlError(error);
  }
};
